import { Menu, LogOut, User, ChevronDown, Building2, CheckCircle2, Layers, BarChart3, PanelLeftOpen, PanelLeftClose, MessageCircle, Sun, Moon } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationBell from '../notifications/NotificationBell';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { getUnreadCounts } from '../../lib/messaging/queries';
import { supabase } from '../../lib/supabase';

interface HeaderProps {
  onMenuClick: () => void;
  desktopCollapsed: boolean;
  onToggleSidebar: () => void;
}

export default function Header({ onMenuClick, desktopCollapsed, onToggleSidebar }: HeaderProps) {
  const { profile, signOut } = useAuth();
  const { companies, activeCompany, setActiveCompany, isHoldingView, isConsolidatedView } = useCompany();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Unread direct/channel messages → badge on the messages icon.
  const userId = profile?.id ?? '';
  const { data: unreadChannels } = useQuery({
    queryKey: ['msg-unread-total', userId],
    queryFn: () => getUnreadCounts(userId),
    enabled: !!userId,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
  const unreadMessages = (unreadChannels ?? []).reduce((s, c) => s + Number(c.unread_count || 0), 0);

  // Bump the badge instantly when any new message arrives.
  useEffect(() => {
    if (!userId) return;
    const ch = supabase
      .channel('hdr-msg-unread')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'msg_messages' }, () => {
        queryClient.invalidateQueries({ queryKey: ['msg-unread-total', userId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, queryClient]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setShowCompanyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const displayName = isConsolidatedView
    ? t('header_consolidated_view')
    : (activeCompany?.company_name ?? '');

  const hasMultipleCompanies = companies.length > 1;

  // full_name defaults to '' in the DB, so fall back to username, then email, before a generic label.
  const userLabel = profile?.full_name?.trim()
    || profile?.username?.trim()
    || profile?.email?.split('@')[0]
    || 'User';

  return (
    <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors p-1"
        >
          <Menu className="w-5 h-5" />
        </button>
        <button
          onClick={onToggleSidebar}
          className="hidden lg:flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
          title={desktopCollapsed ? t('header_show_sidebar', { defaultValue: 'Show sidebar' }) : t('header_hide_sidebar', { defaultValue: 'Hide sidebar' })}
        >
          {desktopCollapsed
            ? <PanelLeftOpen className="w-4 h-4" />
            : <PanelLeftClose className="w-4 h-4" />
          }
        </button>

        {hasMultipleCompanies && (
          <div className="relative" ref={companyDropdownRef}>
            <button
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              className={`flex items-center gap-2 ps-2.5 pe-3 py-1.5 rounded-lg border transition-all text-sm ${
                isConsolidatedView
                  ? 'bg-teal-950 border-teal-700 hover:border-teal-600'
                  : isHoldingView
                  ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600'
                  : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600'
              }`}
            >
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                isConsolidatedView ? 'bg-teal-500/20' : isHoldingView ? 'bg-amber-100 dark:bg-amber-500/20' : 'bg-blue-500/20'
              }`}>
                {isConsolidatedView
                  ? <BarChart3 className="w-3 h-3 text-teal-400" />
                  : isHoldingView
                  ? <Layers className="w-3 h-3 text-amber-800 dark:text-amber-400" />
                  : <Building2 className="w-3 h-3 text-blue-400" />
                }
              </div>
              <span className={`font-medium hidden sm:block max-w-44 truncate ${
                isConsolidatedView ? 'text-teal-300' : isHoldingView ? 'text-amber-800 dark:text-amber-300' : 'text-slate-700 dark:text-slate-200'
              }`}>
                {displayName}
              </span>
              {isConsolidatedView && (
                <span className="hidden sm:inline-flex items-center text-xs bg-teal-500/15 text-teal-400 px-1.5 py-0.5 rounded-full font-medium">
                  {t('header_all_companies')}
                </span>
              )}
              {isHoldingView && !isConsolidatedView && (
                <span className="hidden sm:inline-flex items-center text-xs bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
                  {t('header_consolidated')}
                </span>
              )}
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''} ${
                isConsolidatedView ? 'text-teal-500' : isHoldingView ? 'text-amber-800 dark:text-amber-500' : 'text-slate-600 dark:text-slate-500'
              }`} />
            </button>

            {showCompanyDropdown && (
              <div className="absolute start-0 top-full mt-1.5 w-80 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in">
                <div className="px-3 py-2.5 border-b border-slate-300 dark:border-slate-700">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-500 uppercase tracking-wider">{t('header_switch_company')}</p>
                </div>
                <div className="p-1.5 max-h-80 overflow-y-auto">
                  <button
                    onClick={() => {
                      setActiveCompany(null);
                      setShowCompanyDropdown(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-start mb-0.5 ${
                      isConsolidatedView
                        ? 'bg-teal-500/10 border border-teal-500/30'
                        : 'hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-500/20">
                      <BarChart3 className="w-4 h-4 text-teal-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isConsolidatedView ? 'text-teal-300' : 'text-slate-700 dark:text-slate-200'}`}>
                        {t('header_consolidated_view')}
                      </p>
                      <p className="text-xs text-teal-600">{t('header_consolidated_desc')}</p>
                    </div>
                    {isConsolidatedView && (
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-teal-400" />
                    )}
                  </button>

                  <div className="border-t border-slate-300/60 dark:border-slate-700/60 my-1.5" />

                  {companies.map(company => {
                    const isActive = !isConsolidatedView && company.id === activeCompany?.id;
                    const isHolding = company.company_type === 'holding';
                    return (
                      <button
                        key={company.id}
                        onClick={() => {
                          setActiveCompany(company);
                          setShowCompanyDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-start ${
                          isActive
                            ? isHolding
                              ? 'bg-amber-100 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20'
                              : 'bg-blue-500/10 border border-blue-500/20'
                            : 'hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isHolding ? 'bg-amber-100 dark:bg-amber-500/20' : 'bg-blue-500/20'}`}>
                          {isHolding
                            ? <Layers className="w-4 h-4 text-amber-800 dark:text-amber-400" />
                            : <Building2 className="w-4 h-4 text-blue-400" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${isActive ? (isHolding ? 'text-amber-800 dark:text-amber-300' : 'text-blue-300') : 'text-slate-700 dark:text-slate-200'}`}>
                            {company.company_name}
                          </p>
                          <p className={`text-xs ${isHolding ? 'text-amber-800 dark:text-amber-500' : 'text-slate-600 dark:text-slate-500'}`}>
                            {isHolding ? t('header_holding_group') : t('header_subsidiary')}
                          </p>
                        </div>
                        {isActive && (
                          <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${isHolding ? 'text-amber-800 dark:text-amber-400' : 'text-blue-400'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="px-3 py-2 border-t border-slate-300 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-800/50">
                  <p className="text-xs text-slate-700 dark:text-slate-600">
                    {t('header_consolidated_footer')}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {!hasMultipleCompanies && activeCompany && (
          <div className="flex items-center gap-2 ps-2.5 pe-3 py-1.5 rounded-lg bg-slate-100/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 text-sm">
            <div className="w-5 h-5 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-3 h-3 text-blue-400" />
            </div>
            <span className="text-slate-600 dark:text-slate-300 font-medium hidden sm:block max-w-40 truncate">
              {activeCompany.company_name}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/messages')}
          className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          title={unreadMessages > 0 ? `${unreadMessages} ${t('hdr_unread_messages', 'unread messages')}` : t('nav_messages', 'Messages')}
        >
          <MessageCircle className="w-4.5 h-4.5" />
          {unreadMessages > 0 && (
            <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadMessages > 99 ? '99+' : unreadMessages}
            </span>
          )}
        </button>
        <button
          onClick={toggleTheme}
          className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          title={theme === 'dark' ? t('header_switch_to_light', { defaultValue: 'Switch to light mode' }) : t('header_switch_to_dark', { defaultValue: 'Switch to dark mode' })}
        >
          {theme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
        </button>
        <LanguageSwitcher />
        <NotificationBell />

        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex items-center gap-2 ps-2 pe-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-all text-sm"
          >
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {userLabel.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-slate-600 dark:text-slate-300 font-medium hidden sm:block max-w-32 truncate">
              {userLabel}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-600 dark:text-slate-500 hidden sm:block" />
          </button>

          {showUserDropdown && (
            <div className="absolute end-0 top-full mt-1 w-52 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden z-50 animate-fade-in">
              <div className="p-3 border-b border-slate-300 dark:border-slate-700">
                <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{userLabel}</p>
                <p className="text-slate-600 dark:text-slate-500 text-xs truncate">{profile?.email}</p>
                <span className="inline-block mt-1.5 text-xs bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full capitalize">
                  {profile?.role.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="p-1">
                <button
                  onClick={() => { setShowUserDropdown(false); navigate('/my-profile'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-all"
                >
                  <User className="w-4 h-4" />
                  {t('header_my_profile')}
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  {t('header_sign_out')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
